import { Controller } from "@hotwired/stimulus"

// Phase 2.5 Tier 4 — view-only portfolio CIP Timeline (Gantt).
//
// We deliberately do NOT pull in Bryntum or Frappe Gantt here:
//   - Bryntum is licensed and editor-grade — over-built for a 30-bar
//     view-only chart.
//   - Frappe Gantt isn't vendored and adds an external dependency just
//     to render rectangles.
// Instead we draw the bars directly with absolutely-positioned divs on
// a CSS-grid time axis. Lightweight, theme-able, and entirely under our
// own control. The plan explicitly flagged "*check before pulling in a
// new dependency*" — so this implementation stays in-house.
//
// Tooltip + drill-through are vanilla event handlers; row count is the
// number of project bars (≤ 200 by aggregator cap), well under any DOM
// budget.
export default class extends Controller {
  static values = { payload: Object }
  static targets = ["container", "placeholder"]

  connect() {
    this._render()
  }

  _render() {
    const data = this.payloadValue
    if (!data || !Array.isArray(data.bars)) return
    if (this.hasPlaceholderTarget) this.placeholderTarget.remove()

    const start = new Date(data.start_date)
    const end = new Date(data.end_date)
    const totalDays = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)))
    const today = new Date(data.as_of)
    const todayPct = Math.max(0, Math.min(100, ((today - start) / (end - start)) * 100))

    const yearTicks = this._yearTicks(start, end)

    const html = `
      <div class="cip-timeline">
        <div class="cip-timeline__axis">
          <div class="cip-timeline__row-label"></div>
          <div class="cip-timeline__axis-grid">
            ${yearTicks.map(t => `
              <div class="cip-timeline__year-tick" style="left:${t.pct}%">
                <span class="text-[11px] text-gray-500">${t.year}</span>
              </div>
            `).join("")}
            <div class="cip-timeline__today-line" style="left:${todayPct}%"></div>
          </div>
        </div>
        <div class="cip-timeline__rows">
          ${data.bars.map(bar => this._renderRow(bar, start, totalDays, todayPct)).join("")}
        </div>
      </div>
    `
    this.containerTarget.insertAdjacentHTML("beforeend", html)

    this._attachTooltips()
  }

  _renderRow(bar, start, totalDays, todayPct) {
    const barStart = new Date(bar.start_date)
    const barEnd = new Date(bar.end_date)
    const startDays = Math.max(0, Math.round((barStart - start) / (1000 * 60 * 60 * 24)))
    const spanDays = Math.max(1, Math.round((barEnd - barStart) / (1000 * 60 * 60 * 24)))
    const startPct = (startDays / totalDays) * 100
    const widthPct = Math.max(0.5, (spanDays / totalDays) * 100)

    const formatted = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    const tooltip = [
      `${bar.project_name}`,
      bar.fund_name ? `Primary fund: ${bar.fund_name}` : "Unallocated",
      `Allocated: ${formatted(bar.allocated)}`,
      `Released: ${formatted(bar.released)}`,
      `Spent: ${formatted(bar.spent)}`,
      bar.status ? `Status: ${bar.status}` : null
    ].filter(Boolean).join("\n")

    const projectUrl = `/projects/${bar.project_id}/funding`

    return `
      <a class="cip-timeline__row" href="${projectUrl}" title="${this._escape(tooltip)}">
        <div class="cip-timeline__row-label">
          <span class="truncate">${this._escape(bar.project_name)}</span>
        </div>
        <div class="cip-timeline__row-axis">
          <div class="cip-timeline__today-line cip-timeline__today-line--row" style="left:${todayPct}%"></div>
          <div class="cip-timeline__bar"
               style="left:${startPct}%; width:${widthPct}%; background:${bar.color};">
          </div>
        </div>
      </a>
    `
  }

  _yearTicks(start, end) {
    const ticks = []
    const startYear = start.getFullYear()
    const endYear = end.getFullYear()
    const totalMs = end - start
    for (let y = startYear; y <= endYear; y += 1) {
      const yearStart = new Date(y, 0, 1)
      if (yearStart < start) continue
      const pct = ((yearStart - start) / totalMs) * 100
      ticks.push({ year: y, pct })
    }
    return ticks
  }

  _attachTooltips() {
    // Browser-native title attribute is sufficient for a view-only
    // chart; pricier tooltips defer until product surfaces a need.
  }

  _escape(str) {
    return String(str || "").replace(/[&<>"']/g, ch => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[ch]))
  }
}
